import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, q, explain } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useAsync } from '../lib/useAsync';
import { ensureProfile } from '../lib/profile';
import { EmptyState, ErrorBanner, Modal, Spinner } from '../components/Shared';
import Icon from '../components/Icon';

function genJoinCode() {
  // No O/0/I/1 — these get read aloud in a noisy changing room.
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export default function GroupsScreen() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  const [claiming, setClaiming] = useState(null);

  const { data: groups, loading, error, reload } = useAsync(async () => {
    await ensureProfile(user);
    const rows = await q(
      supabase
        .from('group_members')
        .select('group_id, role, groups(id, name, join_code, created_at)')
        .eq('profile_id', user.id),
      'Loading your groups'
    );
    return (rows || [])
      .filter((r) => r.groups)
      .map((r) => ({ ...r.groups, role: r.role }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [user.id]);

  const createGroup = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setFormError('Give it a name.');
      return;
    }
    setBusy(true);
    setFormError('');
    try {
      const group = await q(
        supabase
          .from('groups')
          .insert({ name: name.trim(), join_code: genJoinCode(), created_by: user.id })
          .select()
          .single(),
        'Creating the group'
      );
      await q(
        supabase.from('group_members').insert({ group_id: group.id, profile_id: user.id, role: 'admin' }),
        'Adding you as admin'
      );
      setName('');
      setMode(null);
      navigate(`/g/${group.id}/squad`);
    } catch (err) {
      setFormError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const joinGroup = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setFormError('Enter a join code.');
      return;
    }
    setBusy(true);
    setFormError('');
    try {
      const group = await q(
        supabase.from('groups').select('id, name').eq('join_code', code.trim().toUpperCase()).maybeSingle(),
        'Finding that group'
      );
      if (!group) {
        setFormError('No group found with that code. Codes are 6 characters and case-insensitive.');
        return;
      }
      const already = await q(
        supabase
          .from('group_members')
          .select('id')
          .eq('group_id', group.id)
          .eq('profile_id', user.id)
          .maybeSingle(),
        'Checking your membership'
      );

      if (already) {
        setCode('');
        setMode(null);
        navigate(`/g/${group.id}/squad`);
        return;
      }

      // Ask who they are BEFORE creating a row. If somebody has already been
      // added to this squad as a guest, claiming that row keeps the games,
      // goals and rating recorded against their name. Creating a row first and
      // merging afterwards would mean reconciling two histories.
      const guests = await q(
        supabase.rpc('list_unclaimed_members', { gid: group.id }),
        'Checking for an existing player'
      );

      if (guests?.length) {
        setClaiming({ group, guests });
        return;
      }

      await q(
        supabase.from('group_members').insert({ group_id: group.id, profile_id: user.id, role: 'member' }),
        'Joining the group'
      );
      setCode('');
      setMode(null);
      navigate(`/g/${group.id}/squad`);
    } catch (err) {
      setFormError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  // Either link this account to an existing guest row, or create a fresh one.
  const finishJoin = async (guestId) => {
    setBusy(true);
    setFormError('');
    try {
      if (guestId) {
        await q(supabase.rpc('claim_group_member', { target: guestId }), 'Claiming that player');
      } else {
        await q(
          supabase
            .from('group_members')
            .insert({ group_id: claiming.group.id, profile_id: user.id, role: 'member' }),
          'Joining the group'
        );
      }
      const groupId = claiming.group.id;
      setClaiming(null);
      setCode('');
      setMode(null);
      navigate(`/g/${groupId}/squad`);
    } catch (err) {
      setFormError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async (group) => {
    try {
      await navigator.clipboard.writeText(group.join_code);
      setCopied(group.id);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      /* clipboard blocked — the code is on screen anyway */
    }
  };

  return (
    <div className="page page--narrow">
      <header className="page__head">
        <div>
          <h1 className="page__title">YOUR GROUPS</h1>
          <p className="page__sub">{user.email}</p>
        </div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={signOut}>
          <Icon name="logout" size={15} />
          Sign out
        </button>
      </header>

      <ErrorBanner error={error} onRetry={reload} />

      {loading && <Spinner label="Loading your groups…" />}

      {!loading &&
        (groups || []).map((g) => (
          <div key={g.id} className="group-card">
            <button type="button" className="group-card__main" onClick={() => navigate(`/g/${g.id}/squad`)}>
              <span className="group-card__name">{g.name}</span>
              <span className="group-card__meta">{g.role === 'admin' ? 'Admin' : 'Member'}</span>
            </button>
            <button
              type="button"
              className="group-card__code"
              onClick={() => copyCode(g)}
              title="Copy invite code"
            >
              <span>{copied === g.id ? 'Copied' : g.join_code}</span>
              <Icon name={copied === g.id ? 'check' : 'copy'} size={14} />
            </button>
            <Icon name="chevronRight" size={18} className="group-card__chevron" />
          </div>
        ))}

      {!loading && !error && groups?.length === 0 && (
        <EmptyState text="No groups yet. Create one for your regular game, or join an existing one with its 6-character code." />
      )}

      {mode === null && (
        <div className="row row--gap">
          <button type="button" className="btn btn--primary" onClick={() => setMode('create')}>
            Create group
          </button>
          <button type="button" className="btn btn--outline" onClick={() => setMode('join')}>
            Join with code
          </button>
        </div>
      )}

      {mode === 'create' && (
        <form className="card stack" onSubmit={createGroup}>
          <label className="field">
            <span className="field__label">Group name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Thursday night football"
              autoFocus
            />
          </label>
          {formError && <p className="form-error">{formError}</p>}
          <div className="row row--gap">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setMode(null);
                setFormError('');
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {claiming && (
        <Modal
          title={`Joining ${claiming.group.name}`}
          subtitle="Are you already in this squad? Pick your name and your games, goals and rating come with you."
          onClose={() => {
            setClaiming(null);
            setFormError('');
          }}
        >
          {formError && <p className="form-error">{formError}</p>}
          <div className="pick">
            {claiming.guests.map((g) => (
              <button
                type="button"
                key={g.id}
                className="pick__chip"
                onClick={() => finishJoin(g.id)}
                disabled={busy}
              >
                {g.display_name || 'Unnamed'}
              </button>
            ))}
          </div>
          <p className="field__note">
            Only players nobody has claimed are listed. If your name isn't here, someone has already
            taken it — ask an admin.
          </p>
          <button
            type="button"
            className="btn btn--outline btn--block"
            style={{ marginTop: 14 }}
            onClick={() => finishJoin(null)}
            disabled={busy}
          >
            I'm new to this group
          </button>
        </Modal>
      )}

      {mode === 'join' && (
        <form className="card stack" onSubmit={joinGroup}>
          <label className="field">
            <span className="field__label">Join code</span>
            <input
              className="input input--code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABC123"
              maxLength={6}
              autoFocus
            />
          </label>
          {formError && <p className="form-error">{formError}</p>}
          <div className="row row--gap">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setMode(null);
                setFormError('');
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Joining…' : 'Join'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
